// components/portal/tables/deals/DealsTable.tsx
'use client';

import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { EntityView } from '@/components/entities/EntityView';
import { createScopedOrgMemberToolbarExtras } from '@/components/entities/tables/ScopedOrgMemberToolbarExtras';
import { useScopedOrgMemberFilters } from '@/components/entities/tables/useScopedOrgMemberFilters';
import { InventoryEntityState, LeasingDealFilters } from '#types/types';
import { Table, TableColumnDef } from '../../../portal/Table';
import { Activity, Coins, Grid2x2, Hash, Landmark, Presentation, Store, User } from '@/components/ui/lucide-memo';

const translations = {
  en: {
    sortLastUpdated: 'Last Updated', sortCreatedDate: 'Created Date', sortStatus: 'Status',
    sortRentAmount: 'Rent Amount',
    tenantBrand: 'Tenant / Brand', code: 'Code', unit: 'Unit', property: 'Property', rent: 'Rent',
    status: 'Status', projects: 'Projects', assignee: 'Assignee',
  },
  fr: {
    sortLastUpdated: 'Dernière maj', sortCreatedDate: 'Date création', sortStatus: 'Statut',
    sortRentAmount: 'Montant loyer',
    tenantBrand: 'Locataire / Marque', code: 'Code', unit: 'Unité', property: 'Bien', rent: 'Loyer',
    status: 'Statut', projects: 'Projets', assignee: 'Assigné',
  },
};

export const DealsTable = React.memo(({
  lockedFilters,
  assetLedgerTitle = 'All deals',
  assetLedgerBreadcrumb = 'Inventory / Deals',
  showDefaultToolbarSelectors = true,
  renderCustomToolbarHeader,
}: {
  lockedFilters?: Partial<LeasingDealFilters>;
  assetLedgerTitle?: string;
  assetLedgerBreadcrumb?: string;
  showDefaultToolbarSelectors?: boolean;
  renderCustomToolbarHeader?: () => React.ReactNode;
}) => {
  const { kernel } = useKernel();
  const lang = useKernelStore((state) => state.language);
  const t = translations[lang];

  // Activité pour déclencher les effets d'hydratation
  const dealLastActivity = useKernelStore(state => state.inventory.Deal.lastActivity);
  const unitLastActivity = useKernelStore(state => state.inventory.Unit.lastActivity);
  
  const [visibleDealIds, setVisibleDealIds] = useState<string[]>([]);
  const handleVisibleIdsChange = useCallback((ids: string[]) => setVisibleDealIds(ids), []);

  // 1. Effet A : Hydrater Marques et Unités pour les Deals visibles
  useEffect(() => {
    const k = kernel.current;
    if (!k || visibleDealIds.length === 0) return;

    const inv = k.getInventory();
    const dealReg = inv.Deal.registry;
    const brandReg = inv.Brand.registry;
    const unitReg = inv.Unit.registry;

    const brandIdsToFetch = new Set<string>();
    const unitIdsToFetch = new Set<string>();

    visibleDealIds.forEach(id => {
      const deal = dealReg[id];
      if (!deal) return;
      if (deal.brandId && !brandReg[deal.brandId]) brandIdsToFetch.add(deal.brandId);
      if (deal.unitId && !unitReg[deal.unitId]) unitIdsToFetch.add(deal.unitId);
    });

    if (brandIdsToFetch.size > 0) k.ensureInventoryIds('Brand', Array.from(brandIdsToFetch));
    if (unitIdsToFetch.size > 0) k.ensureInventoryIds('Unit', Array.from(unitIdsToFetch));

  }, [visibleDealIds, dealLastActivity, kernel]);

  // 2. Effet B : Hydrater les Propriétés pour les Unités des Deals visibles
  useEffect(() => {
    const k = kernel.current;
    if (!k || visibleDealIds.length === 0) return;

    const inv = k.getInventory();
    const dealReg = inv.Deal.registry;
    const unitReg = inv.Unit.registry;
    const propReg = inv.Property.registry;

    const propertyIdsToFetch = new Set<string>();

    visibleDealIds.forEach(id => {
      const deal = dealReg[id];
      const unit = deal?.unitId ? unitReg[deal.unitId] : null;
      if (unit?.propertyId && !propReg[unit.propertyId]) {
        propertyIdsToFetch.add(unit.propertyId);
      }
    });

    if (propertyIdsToFetch.size > 0) k.ensureInventoryIds('Property', Array.from(propertyIdsToFetch));

  }, [visibleDealIds, unitLastActivity, kernel]);

  // --- Filtres et Logique Standard ---
  const userContext = useKernelStore((state) => state.userContext);
  const orgRegistry = useKernelStore((state) => state.inventory.Organisation.registry);
  const currentOrg = userContext.organisationId ? orgRegistry[userContext.organisationId] : null;
  const shouldSkipDefaultOrganisationFilter =
    userContext.isFaterAdmin ||
    userContext.isFaterOrgMember ||
    currentOrg?.name?.trim().toLowerCase() === 'fater';

  const [tableFilters, setTableFilters] = useState<Partial<LeasingDealFilters>>(() => ({
    ...(lockedFilters || {}),
    organisationId: lockedFilters?.organisationId ?? (shouldSkipDefaultOrganisationFilter ? undefined : currentOrg?.id ?? undefined),
  }));

  const {
    selectedOrgId, activeOrgId, selectedUser, hasDefaultToolbarSelectors,
    showOrganisationSelector, showUserSelector, handleOrgSelect, handleUserSelect,
  } = useScopedOrgMemberFilters<LeasingDealFilters>({
    tableFilters, setTableFilters, lockedFilters, userFilterKey: 'assignedUserId', showDefaultToolbarSelectors,
  });

  const sortOptions = useMemo(() => [
    { label: t.sortLastUpdated, field: 'updatedAt' as const },
    { label: t.sortCreatedDate, field: 'createdAt' as const },
    { label: t.sortStatus, field: 'status' as const },
    { label: t.sortRentAmount, field: 'rentAmount' as const },
  ], [t]);

  const columns = useMemo<TableColumnDef[]>(() => [
    { id: 'brand', icon: Store, minWidth: 200, header: t.tenantBrand },
    { id: 'code', icon: Hash, minWidth: 140, header: t.code },
    { id: 'unit', icon: Grid2x2, minWidth: 80, header: t.unit },
    { id: 'property', icon: Landmark, minWidth: 120, header: t.property },
    { id: 'rent', icon: Coins, minWidth: 100, header: t.rent },
    { id: 'status', icon: Activity, minWidth: 100, header: t.status },
    { id: 'projects', icon: Presentation, minWidth: 80, header: t.projects },
    { id: 'assignee', icon: User, minWidth: 120, header: t.assignee },
    { id: 'actions', minWidth: 80, header: '', align: 'right' },
  ], [t]);

  const renderToolbarExtras = useMemo(() => createScopedOrgMemberToolbarExtras({
    renderCustomToolbarHeader, hasDefaultToolbarSelectors, showOrganisationSelector, showUserSelector,
    selectedOrgId, selectedUser, activeOrgId: activeOrgId ?? undefined,
    onOrgSelect: handleOrgSelect, onUserSelect: handleUserSelect,
  }), [
    renderCustomToolbarHeader, hasDefaultToolbarSelectors, showOrganisationSelector, showUserSelector,
    selectedOrgId, selectedUser, activeOrgId, handleOrgSelect, handleUserSelect,
  ]);

  const renderRow = useCallback((
    deal: InventoryEntityState<'Deal'>,
    isExpanded: boolean,
    toggle: () => void,
    currentColumns?: TableColumnDef[],
  ) => (
    <div className="flex w-full flex-none flex-col">
      <EntityView
        type="Deal"
        entityId={deal.id}
        variant="row"
        onClick={toggle}
        columns={currentColumns}
        className={isExpanded ? 'bg-white/[0.02] border-white/10' : ''}
      />
      {isExpanded && (
        <EntityView
          type="Deal"
          entityId={deal.id}
          variant="page"
          pageHasEntityRowAbove
          className="h-auto border-b border-[#2A2A2A]"
        />
      )}
    </div>
  ), []);

  return (
    <Table
      entityType="Deal"
      initialFilters={tableFilters}
      lockedFilters={lockedFilters}
      columns={columns}
      assetLedgerTitle={assetLedgerTitle}
      assetLedgerBreadcrumb={assetLedgerBreadcrumb}
      sortOptions={sortOptions}
      onVisibleItemIdsChange={handleVisibleIdsChange}
      renderToolbarExtras={renderToolbarExtras}
      renderRow={renderRow}
    />
  );
});

DealsTable.displayName = 'DealsTable';
